// End-of-season summary of the minor league season. Individual awards come only from minor league lines
// (p.minorStats), never from major league stats:
//   MVP:          best hitter by production above average, PA * (OPS - league OPS)
//   Best pitcher: most runs saved, IP/9 * (league ERA - ERA)
//   HR leader:    most home runs (ties: higher OPS)
//   All-Minors:   best hitter at each position (his position at the end of the season), a DH, five SP and two RP
// Competition: final standings of the head-to-head games (teamSeason.minorLeague), in the standings order. The best
// record is "champion by record" and the next one "second by record" (there are no playoffs or final), and only when
// the season's games were all recorded: every organization has a record that started with the season and has as
// many games as its big club. Otherwise the partial standings are kept with the reason and nobody gets a title.
import { PLAYER } from "../../../common/constants.ts";
import { minorLine } from "../../../common/minorStats.ts";
import type { MinorLeagueSummary, Player } from "../../../common/types.ts";
import { compareMinorStandings, minorLeagueHype } from "../../../common/minorStandings.ts";
import { idb } from "../../db/index.ts";
import { g, helpers } from "../../util/index.ts";

const FIELD_POSITIONS = ["C", "1B", "2B", "3B", "SS", "LF", "CF", "RF"];
const PITCHERS = new Set(["SP", "RP"]);
const round = (x: number, d = 3) => Math.round(x * 10 ** d) / 10 ** d;

export const computeMinorCompetition = async (season: number): Promise<MinorLeagueSummary["competition"]> => {
	const teams = (await idb.cache.teams.getAll()).filter((t) => !t.disabled);
	const rows = [];
	let startedMidSeason = false, missingGames = false, mismatch = false;
	for (const t of teams) {
		const ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [t.tid, season]);
		const r = ts?.minorLeague;
		if (r?.startedMidSeason) startedMidSeason = true;
		if (!r || r.g === 0) missingGames = true;
		const bigClubGames = ts ? ts.won + ts.lost + ts.tied + ts.otl : 0;
		if (r && r.g !== bigClubGames) mismatch = true;
		rows.push({
			tid: t.tid,
			abbrev: ts?.abbrev ?? t.abbrev,
			region: ts?.region ?? t.region,
			name: ts?.name ?? t.name,
			g: r?.g ?? 0,
			w: r?.w ?? 0,
			l: r?.l ?? 0,
			rs: r?.rs ?? 0,
			ra: r?.ra ?? 0,
		});
	}
	if (!rows.some((r) => r.g > 0)) {
		return undefined; // no head-to-head games that season (old saves)
	}
	rows.sort(compareMinorStandings);
	const reason = startedMidSeason ? "startedMidSeason" : missingGames ? "missingGames" : mismatch ? "incomplete" : undefined;
	return {
		complete: reason === undefined,
		...(reason ? { reason } : { champion: rows[0]!.tid, runnerUp: rows[1]?.tid }),
		standings: rows,
	};
};

// Apply only a complete final season, once per organization even if awards are recalculated.
export const applyMinorLeagueHype = async (
	season: number,
	competition: MinorLeagueSummary["competition"],
) => {
	if (!competition?.complete) return;
	for (const [i, row] of competition.standings.entries()) {
		const ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [row.tid, season]);
		if (!ts?.minorLeague || ts.minorLeague.hype !== undefined) continue;
		const hype = minorLeagueHype(i + 1, competition.standings.length);
		ts.hype = helpers.bound(ts.hype + hype, 0, 1);
		ts.minorLeague.hype = hype;
		await idb.cache.teamSeasons.put(ts);
	}
};

export const computeMinorLeagueSummary = async (season: number): Promise<MinorLeagueSummary | undefined> => {
	const players = (await idb.cache.players.getAll()).filter((p) => p.minorStats?.season === season);
	const competition = await computeMinorCompetition(season);
	if (players.length === 0 && !competition) {
		return undefined;
	}
	const rows = players.map((p) => ({ p, s: p.minorStats!, l: minorLine(p.minorStats!) }));

	// League context from summed components
	const sum = (k: keyof NonNullable<Player["minorStats"]>) => rows.reduce((a, x) => a + ((x.s[k] as number) ?? 0), 0);
	const pa = sum("pa"), ab = sum("ab"), h = sum("h"), bb = sum("bb"), outs = sum("outs"), er = sum("er");
	const tb = sum("h") + sum("d") + 2 * sum("t") + 3 * sum("hr");
	const lgOps = (pa > 0 ? (h + bb) / pa : 0) + (ab > 0 ? tb / ab : 0);
	const lgEra = outs > 0 ? (27 * er) / outs : 0;

	const org = (p: Player) => (p.tid === PLAYER.MINORS ? (p.minorTid ?? p.tid) : p.tid);
	const entry = (x: (typeof rows)[number], score: number, pitcher: boolean) => {
		const tid = org(x.p);
		return {
			pid: x.p.pid,
			name: `${x.p.firstName} ${x.p.lastName}`,
			pos: x.p.ratings.at(-1)!.pos,
			age: season - x.p.born.year,
			tid,
			abbrev: g.get("teamInfoCache")[tid]?.abbrev ?? "",
			score: round(score, 2),
			stats: (pitcher
				? { g: x.l.gp, gs: x.s.gs ?? 0, ip: x.l.ip, so: x.l.soP, era: round(x.l.era, 2), whip: round(x.l.whip, 2) }
				: { g: x.l.g, pa: x.l.pa, h: x.l.h, hr: x.l.hr, rbi: x.l.rbi, avg: round(x.l.avg), ops: round(x.l.ops) }) as Record<string, number>,
		};
	};

	const maxPa = Math.max(0, ...rows.map((x) => x.l.pa));
	const maxOuts = Math.max(0, ...rows.map((x) => x.l.outs));
	const hitters = rows.filter((x) => !PITCHERS.has(x.p.ratings.at(-1)!.pos) && x.l.pa > 0);
	const pitchers = rows.filter((x) => x.l.outs > 0);
	const hitScore = (x: (typeof rows)[number]) => x.l.pa * (x.l.ops - lgOps);
	const pitchScore = (x: (typeof rows)[number]) => (x.l.outs / 27) * (lgEra - x.l.era);
	const qualifiedHitters = hitters.filter((x) => x.l.pa >= 0.5 * maxPa);
	const qualifiedPitchers = pitchers.filter((x) => x.l.outs >= 0.5 * maxOuts);
	const best = <T>(list: T[], score: (x: T) => number) => [...list].sort((a, b) => score(b) - score(a))[0];

	const mvp = best(qualifiedHitters, hitScore);
	const ace = best(qualifiedPitchers, pitchScore);
	const hr = [...hitters].sort((a, b) => b.l.hr - a.l.hr || b.l.ops - a.l.ops)[0];

	// All-Minors team: one player per role, nobody twice
	const used = new Set<number>();
	const team: MinorLeagueSummary["team"] = [];
	const regularHitters = hitters.filter((x) => x.l.pa >= 0.3 * maxPa);
	for (const pos of FIELD_POSITIONS) {
		const pick = best(regularHitters.filter((x) => x.p.ratings.at(-1)!.pos === pos && !used.has(x.p.pid)), hitScore);
		if (pick) {
			used.add(pick.p.pid);
			team.push({ role: pos, player: entry(pick, hitScore(pick), false) });
		}
	}
	const dh = best(regularHitters.filter((x) => !used.has(x.p.pid)), hitScore);
	if (dh) {
		used.add(dh.p.pid);
		team.push({ role: "DH", player: entry(dh, hitScore(dh), false) });
	}
	const starters = pitchers.filter((x) => (x.s.gs ?? 0) >= x.l.gp / 2 && x.l.outs >= 0.3 * maxOuts);
	const relievers = pitchers.filter((x) => (x.s.gs ?? 0) < x.l.gp / 2);
	const maxRelieverOuts = Math.max(0, ...relievers.map((x) => x.l.outs));
	for (let i = 0; i < 5; i++) {
		const pick = best(starters.filter((x) => !used.has(x.p.pid)), pitchScore);
		if (!pick) break;
		used.add(pick.p.pid);
		team.push({ role: "SP", player: entry(pick, pitchScore(pick), true) });
	}
	for (let i = 0; i < 2; i++) {
		const pick = best(relievers.filter((x) => !used.has(x.p.pid) && x.l.outs >= 0.3 * maxRelieverOuts), pitchScore);
		if (!pick) break;
		used.add(pick.p.pid);
		team.push({ role: "RP", player: entry(pick, pitchScore(pick), true) });
	}

	return {
		format: "statistical",
		numPlayers: players.length,
		league: { avg: round(ab > 0 ? h / ab : 0), ops: round(lgOps), era: round(lgEra, 2), hr: sum("hr"), so: sum("soP") },
		mvp: mvp ? entry(mvp, hitScore(mvp), false) : undefined,
		bestPitcher: ace ? entry(ace, pitchScore(ace), true) : undefined,
		hrLeader: hr ? entry(hr, hitScore(hr), false) : undefined,
		team,
		...(competition ? { competition } : {}),
	};
};
